import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import {FALLBACK_PRICES, formatPrice, getSubscriptionPrices} from "../../components/helpers/PricingHelpers";

const getCustomer = () => {
  const stored = sessionStorage.getItem("customer");
  return stored ? JSON.parse(stored) : null;
};

const subscriptionBadgeColor = (status) => {
  if (status === "active") {
    return "success";
  }

  if (status === "trialing") {
    return "info";
  }

  if (status === "past_due") {
    return "warning";
  }

  return "danger";
};

const roleBadgeColor = (role) => {
  if (role === "OWNER") {
    return "primary";
  }

  if (role === "ADMIN") {
    return "info";
  }

  return "secondary";
};

const roleBadgeStyle = (role) => ({
  color: role === "MEMBER" ? "#000000" : "#ffffff",
  letterSpacing: "0",
});

const roleSelectStyle = {
  width: "auto",
  minWidth: "120px",
  textAlign: "left",
  fontWeight: 600,
  letterSpacing: "0",
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleDateString("en-GB");
};

// For date-only strings (YYYY-MM-DD from LocalDate), appending a local noon time
// avoids the UTC-parse off-by-one issue in BST and other UTC+ timezones.
const formatPeriodDate = (value) => {
  if (!value) {
    return "";
  }

  const str = String(value);
  const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(str) ? str + "T12:00:00" : str;
  return new Date(dateStr).toLocaleDateString("en-GB");
};

const formatCurrency = (pence) => {
  const amount = Number(pence || 0) / 100;
  return amount.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
};

const invoiceBadgeColor = (status) => {
  if (status === "PAID") {
    return "success";
  }

  if (status === "OPEN") {
    return "warning";
  }

  if (status === "VOID" || status === "UNCOLLECTIBLE") {
    return "danger";
  }

  return "secondary";
};

const TeamAccount = () => {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noAccount, setNoAccount] = useState(false);
  const [memberError, setMemberError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("MEMBER");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [revokeError, setRevokeError] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(null);
  const [pendingRoles, setPendingRoles] = useState({});
  const [billingPeriod, setBillingPeriod] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [billingError, setBillingError] = useState(null);
  const [prices, setPrices] = useState(FALLBACK_PRICES);
  const customer = getCustomer();
  const currentUser = customer?.user;
  const currentMember = members.find((m) => m.userId === currentUser?.id);
  const currentRole = currentMember?.role ?? currentUser?.accountRole;
  const canManageMembers = currentRole === "OWNER" || currentRole === "ADMIN";
  const canChangeRoles = currentRole === "OWNER";
  const isEnterprise = account?.accountType === "ENTERPRISE";
  const canManageBilling = currentRole === "OWNER" || currentRole === "ADMIN";
  const accountLabel = isEnterprise ? "account" : "group";
  const subscriptionLabel = isEnterprise ? "Organisation Admin Subscription" : "Group Subscription";
  const subscriptionActive = account?.subscriptionStatus === "active" || account?.subscriptionStatus === "trialing";
  const activeSeats = account?.activeSeats ?? account?.memberCount ?? members.length;
  // For OWNER/ADMIN, invitations state is kept in sync after every revoke/invite operation,
  // so use it directly rather than the stale API snapshot on account.pendingInvitationCount.
  const pendingInvitationCount = canManageMembers
    ? invitations.length
    : (account?.pendingInvitationCount ?? 0);
  const committedSeats = activeSeats + (isEnterprise ? 0 : pendingInvitationCount);
  const maxSeats = account?.maxSeats;
  const seatsAvailable = !maxSeats || committedSeats < maxSeats;
  const canInvite = canManageMembers && subscriptionActive && seatsAvailable;

  useEffect(() => {
    if (!customer) {
      navigate("/sign-in");
      return;
    }

    const loadAccount = async () => {
      try {
        const sessionRole = currentUser?.accountRole;
        const canViewInvitations = sessionRole === "OWNER" || sessionRole === "ADMIN";
        const requests = [
          Api.get("/api/account/me"),
          Api.get("/api/account/members"),
          ...(canViewInvitations ? [Api.get("/api/account/invitations")] : []),
        ];
        const [accountResponse, membersResponse, invitationsResponse] = await Promise.all(requests);
        setAccount(accountResponse.data);
        setMembers(membersResponse.data);
        if (invitationsResponse) setInvitations(invitationsResponse.data || []);
        getSubscriptionPrices().then(setPrices);
        const canViewBilling = canViewInvitations;
        if (accountResponse.data?.accountType === "ENTERPRISE" && canViewBilling) {
          try {
            const [billingResponse, invoicesResponse] = await Promise.all([
              Api.get("/api/account/billing/current-period"),
              Api.get("/api/account/billing/invoices"),
            ]);
            setBillingPeriod(billingResponse.data);
            setInvoices(invoicesResponse.data || []);
          } catch (billingLoadError) {
            setBillingError("Unable to load Organisation billing details.");
          }
        }
      } catch (e) {
        if (e.response?.status === 404) {
          setNoAccount(true);
        } else {
          setError("Unable to load your group. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveMember = async (userId) => {
    setMemberError(null);

    try {
      await Api.delete(`/api/account/members/${userId}`);
      setMembers((current) => current.filter((member) => member.userId !== userId));
    } catch (e) {
      setMemberError("Unable to remove this member. Please try again.");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setMemberError(null);

    try {
      const response = await Api.put(`/api/account/members/${userId}/role`, {
        role: newRole,
      });
      setMembers((current) =>
        current.map((member) =>
          member.userId === userId ? { ...member, ...response.data } : member
        )
      );
      setPendingRoles((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    } catch (e) {
      setMemberError("Unable to update this member's role. Please try again.");
    }
  };

  const handleRevokeInvitation = async (token) => {
    setRevokeError(null);
    setResendSuccess(null);
    try {
      await Api.delete(`/api/account/invite/${token}`);
      setInvitations((current) => current.filter((inv) => inv.token !== token));
    } catch (e) {
      setRevokeError("Unable to revoke this invitation. Please try again.");
    }
  };

  const handleResendInvitation = async (token) => {
    setRevokeError(null);
    setResendSuccess(null);
    try {
      await Api.post(`/api/account/invite/${token}/resend`);
      const invitationsResponse = await Api.get("/api/account/invitations");
      setInvitations(invitationsResponse.data || []);
      setResendSuccess("Invitation resent.");
    } catch (e) {
      setRevokeError("Unable to resend this invitation. Please try again.");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(null);

    const email = inviteEmail.trim();
    if (!email) {
      setInviteError("Please enter an email address.");
      return;
    }

    setInviting(true);

    try {
      await Api.post("/api/account/invite", {
        email,
        role: inviteRole,
      });
      setInviteSuccess("Invitation sent to " + email);
      setInviteEmail("");
      const invitationsResponse = await Api.get("/api/account/invitations");
      setInvitations(invitationsResponse.data || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 409) {
        if (e.response?.data?.code === "SEAT_CAP_REACHED") {
          setInviteError("This group has reached its maximum number of members, have you considered upgrading to an Organisation account?");
        } else {
          setInviteError("A pending invitation already exists for this email");
        }
      } else if (status === 402) {
        setInviteError(`Your ${isEnterprise ? "Organisation admin" : "Group"} subscription is inactive. You cannot invite new members until your subscription is renewed.`);
      } else if (status === 423) {
        setInviteError(
          e.response?.data?.state === "SUSPENDED"
            ? "Your account is suspended. Please resolve your payment to invite new members."
            : "Your account is currently restricted. Please resolve your payment to invite new members."
        );
      } else if (status === 403) {
        setInviteError("You don't have permission to send invitations");
      } else {
        // Network timeout or server error — the invitation may have been
        // saved before the connection failed. Refresh the list to check.
        try {
          const invitationsResponse = await Api.get("/api/account/invitations");
          const refreshed = invitationsResponse.data || [];
          setInvitations(refreshed);
          if (refreshed.some((inv) => inv.email.toLowerCase() === email.toLowerCase())) {
            setInviteEmail("");
            setInviteError("The invitation was created but the email could not be sent. Use the Resend button below to retry.");
          } else {
            setInviteError("Failed to send invitation. Please try again.");
          }
        } catch (_) {
          setInviteError("Failed to send invitation. Please try again.");
        }
      }
    } finally {
      setInviting(false);
    }
  };

  const renderLifecycleBanner = () => {
    if (!isEnterprise) {
      return null;
    }

    const state = account?.enterpriseState;
    const billingLink = canManageBilling ? (
      <>
        {" "}
        <a href="/your-profile/billing-details?recover=true" className="alert-link">Resolve payment</a>
      </>
    ) : null;

    if (state === "PAST_DUE") {
      return (
        <div className="alert alert-warning mb-4">
          Payment outstanding - your seat payment was unsuccessful. Update your payment method to keep your account in good standing.
          {billingLink}
        </div>
      );
    }

    if (state === "RESTRICTED") {
      return (
        <div className="alert alert-warning mb-4">
          Account restricted. We've been unable to collect your seat payment. New invitations and membership changes are paused. Your team can continue planning worship normally. Update your payment method to restore full access.
          {billingLink}
        </div>
      );
    }

    if (state === "SUSPENDED") {
      return (
        <div className="alert alert-danger mb-4">
          Account suspended. Your account has been suspended due to an unresolved payment. Contact your account owner or update your payment method to reactivate. Your worship plans and data are fully preserved.
          {billingLink}
        </div>
      );
    }

    if (state === "RESTORED") {
      return (
        <div className="alert alert-success mb-4">
          Account restored. Your payment has been resolved and full access has been reinstated. Welcome back.
        </div>
      );
    }

    return null;
  };

  const renderMainCard = () => {
    if (loading) {
      return (
        <Card className="card shadow border-0">
          <CardBody className="py-5 text-center">
            <Spinner color="primary" />
          </CardBody>
        </Card>
      );
    }

    if (error) {
      return (
        <Card className="card shadow border-0">
          <CardBody className="py-5">
            <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
              Your group
            </h2>
            <p className="description mt-3">{error}</p>
          </CardBody>
        </Card>
      );
    }

    if (noAccount) {
      return (
        <Card className="card shadow border-0">
          <CardBody className="py-5">
            <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
              Your group
            </h2>
            <p className="description mt-3">
              You are currently on an individual plan. Set up a group subscription to invite others,
              share song-flows, and plan together. Organisation access is available for wider ministry teams.
            </p>
            <div className="d-flex flex-column flex-md-row" style={{ gap: "12px" }}>
              <Button color="primary" href="/your-profile/team/upgrade">
                Set up your group
              </Button>
              <Button color="secondary" href="/your-profile/enterprise/upgrade">
                Set up Organisation
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    }

    return (
      <>
        {renderLifecycleBanner()}

        <Card className="card shadow border-0 mb-4">
          <CardBody className="py-5">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
              <h4 className="mb-3 mb-md-0">{account.name}</h4>
              {canManageBilling && (
                <Button color="primary" href="/your-profile/billing-details">
                  Manage billing
                </Button>
              )}
            </div>
            <p className="description mt-3 mb-2">
              {subscriptionLabel}{" "}
              <Badge color={subscriptionBadgeColor(account.subscriptionStatus)}>
                {account.subscriptionStatus || "not active"}
              </Badge>
            </p>
            {canManageMembers && !subscriptionActive && (isEnterprise || account.subscriptionStatus !== "past_due") && (
              <div className="alert alert-warning mt-3">
                Your {isEnterprise ? "Organisation admin" : "Group"} subscription is inactive. You cannot invite new members until your subscription is renewed.{" "}
                {canManageBilling && <a href="/your-profile/billing-details?recover=true" className="alert-link">Renew subscription</a>}
              </div>
            )}
            {!isEnterprise && canManageBilling && account.subscriptionStatus === "past_due" && (
              <div className="alert alert-warning mt-3">
                Your subscription payment has failed. Update your payment method to avoid service interruption.{" "}
                <a href="/your-profile/billing-details?recover=true" className="alert-link">Manage billing</a>
              </div>
            )}
            <p className="description mb-2">
              Seats: {isEnterprise ? `${activeSeats} seats (no limit)` : `${activeSeats}${maxSeats ? ` of ${maxSeats}` : ""} active seats`}
              {!isEnterprise && pendingInvitationCount > 0 && (
                <> · {pendingInvitationCount} pending invitation{pendingInvitationCount === 1 ? "" : "s"}</>
              )}
            </p>
            <p className="description mb-0">Owner: {account.ownerName || "Not set"}</p>
          </CardBody>
        </Card>

        <Card className="card shadow border-0 mb-4">
          <CardBody className="py-5">
            <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
              Members
            </h2>
            {memberError && <div className="alert alert-warning mt-3">{memberError}</div>}
            {revokeError && <div className="alert alert-warning mt-3">{revokeError}</div>}
            {resendSuccess && <div className="alert alert-success mt-3">{resendSuccess}</div>}
            <Table responsive className="mt-4">
              <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
                <th />
              </tr>
              </thead>
              <tbody>
              {[...members].sort((a, b) => {
                const nameA = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim().toLowerCase();
                const nameB = `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim().toLowerCase();
                if (nameA !== nameB) return nameA.localeCompare(nameB);
                return (a.email ?? "").toLowerCase().localeCompare((b.email ?? "").toLowerCase());
              }).map((member) => {
                const isCurrentUser = member.userId === currentUser?.id;
                const isOwner = member.role === "OWNER";
                const canRemoveMember = currentRole === "OWNER"
                  ? !isOwner && !isCurrentUser
                  : currentRole === "ADMIN" && !isOwner && !isCurrentUser && (isEnterprise || member.role === "MEMBER");

                return (
                  <tr key={member.userId}>
                    <td>{member.firstName} {member.lastName}</td>
                    <td>{member.email}</td>
                    <td>
                      <Badge color={roleBadgeColor(member.role)} style={roleBadgeStyle(member.role)}>{member.role}</Badge>
                      {canChangeRoles && !isOwner && (
                        <div className="mt-2 d-flex align-items-center" style={{ gap: "8px" }}>
                          <select
                            className="dropdown-toggle btn btn-secondary btn-sm shadow-sm"
                            style={roleSelectStyle}
                            value={pendingRoles[member.userId] ?? member.role}
                            onChange={(e) =>
                              setPendingRoles((prev) => ({ ...prev, [member.userId]: e.target.value }))
                            }
                          >
                            {isEnterprise && <option className="dropdown-item" value="OWNER">OWNER</option>}
                            <option className="dropdown-item" value="ADMIN">ADMIN</option>
                            <option className="dropdown-item" value="MEMBER">MEMBER</option>
                          </select>
                          {pendingRoles[member.userId] && pendingRoles[member.userId] !== member.role && (
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => handleRoleChange(member.userId, pendingRoles[member.userId])}
                            >
                              Save
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                    <td>{formatDate(member.joinedAt)}</td>
                    <td className="text-right">
                      {canRemoveMember && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleRemoveMember(member.userId)}
                        >
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </Table>

            {canManageMembers && invitations.length > 0 && (
              <>
                <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase mt-5">
                  Pending invitations
                </h2>
                <Table responsive className="mt-3">
                  <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Expires</th>
                    <th />
                  </tr>
                  </thead>
                  <tbody>
                  {[...invitations].sort((a, b) => (a.email ?? "").localeCompare(b.email ?? "")).map((inv) => (
                    <tr key={inv.id}>
                      <td>{inv.email}</td>
                      <td><Badge color={roleBadgeColor(inv.role)} style={roleBadgeStyle(inv.role)}>{inv.role}</Badge></td>
                      <td>{formatDate(inv.expiresAt)}</td>
                      <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                        <Button color="secondary" size="sm" outline className="mr-2" onClick={() => handleResendInvitation(inv.token)}>
                          Resend
                        </Button>
                        <Button color="danger" size="sm" outline onClick={() => handleRevokeInvitation(inv.token)}>
                          Revoke
                        </Button>
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </Table>
              </>
            )}
          </CardBody>
        </Card>

        {isEnterprise && canManageMembers && (
          <>
            <Card className="card shadow border-0 mb-4">
              <CardBody className="py-5">
                <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
                  Current billing period
                </h2>
                {billingError && <div className="alert alert-warning mt-3">{billingError}</div>}
                {!billingError && billingPeriod && (
                  <Row className="mt-4">
                    <Col md="3">
                      <p className="description mb-1">Period</p>
                      <strong>{formatPeriodDate(billingPeriod.periodStart)} - {formatPeriodDate(billingPeriod.periodEnd)}</strong>
                    </Col>
                    <Col md="3">
                      <p className="description mb-1">Active seats</p>
                      <strong>{billingPeriod.activeSeats}</strong>
                    </Col>
                    <Col md="3">
                      <p className="description mb-1">Peak seats</p>
                      <strong>{billingPeriod.peakSeats}</strong>
                    </Col>
                    <Col md="3">
                      <p className="description mb-1">Estimated invoice</p>
                      <strong>{formatCurrency(billingPeriod.estimatedAmountPence)}</strong>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>

            {invoices.length > 0 && (
              <Card className="card shadow border-0 mb-4">
                <CardBody className="py-5">
                  <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
                    Invoice history
                  </h2>
                  <Table responsive className="mt-4">
                    <thead>
                    <tr>
                      <th>Period</th>
                      <th>Peak Seats</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th />
                    </tr>
                    </thead>
                    <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id || invoice.stripeInvoiceUrl}>
                        <td>{formatPeriodDate(invoice.periodStart)} - {formatPeriodDate(invoice.periodEnd)}</td>
                        <td>{invoice.peakSeats}</td>
                        <td>{formatCurrency(invoice.totalAmountPence)}</td>
                        <td>
                          <Badge color={invoiceBadgeColor(invoice.status)}>{invoice.status}</Badge>
                        </td>
                        <td className="text-right">
                          {invoice.stripeInvoiceUrl && (
                            <Button color="secondary" size="sm" href={invoice.stripeInvoiceUrl} target="_blank" rel="noreferrer">
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            )}
          </>
        )}

        {canChangeRoles && !isEnterprise && subscriptionActive && (
          <Card className="card shadow border-0 mb-4">
            <CardBody className="py-5">
              <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
                Upgrade to Organisation
              </h2>
              <p className="description mt-3">
                Organisation accounts offer unlimited seats for dioceses, denominations, and institutions.
                {!seatsAvailable && ` Your group has reached its seat limit — upgrade to add more members.`}
              </p>
              <p className="description mt-2">
                Organisation admin fee: <strong>{formatPrice(prices.enterprise.annual)} per year</strong>.
              </p>
              <Button color="primary" href="/your-profile/enterprise/upgrade">
                Upgrade to Organisation
              </Button>
            </CardBody>
          </Card>
        )}

        {canManageMembers && seatsAvailable && (
          <Card className="card shadow border-0">
            <CardBody className="py-5">
              <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
                Invite someone
              </h2>
              {!subscriptionActive && (
                <div className="alert alert-warning mt-3">
                  Your {isEnterprise ? "Organisation admin" : "Group"} subscription is inactive. You cannot invite new members until your subscription is renewed.{" "}
                  {canManageBilling && <a href="/your-profile/billing-details?recover=true" className="alert-link">Renew subscription</a>}
                </div>
              )}
              {subscriptionActive && !isEnterprise && !seatsAvailable && (
                <div className="alert alert-warning mt-3">
                  This group has reached its maximum number of members, have you considered upgrading to an Organisation account?
                </div>
              )}
              {inviteError && <div className="alert alert-warning mt-3">{inviteError}</div>}
              {inviteSuccess && <div className="alert alert-success mt-3">{inviteSuccess}</div>}
              {canInvite && <Form className="mt-4" onSubmit={handleInvite}>
                <Row>
                  <Col md="7">
                    <FormGroup>
                      <Label for="inviteEmail">Email</Label>
                      <Input
                        id="inviteEmail"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for="inviteRole">Role</Label>
                      <select
                        id="inviteRole"
                        className="dropdown-toggle btn btn-secondary shadow-sm"
                        style={{ ...roleSelectStyle, width: "100%" }}
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                      >
                        <option className="dropdown-item" value="MEMBER">MEMBER</option>
                        {currentRole === "OWNER" && (
                          <option className="dropdown-item" value="ADMIN">ADMIN</option>
                        )}
                      </select>
                    </FormGroup>
                  </Col>
                  <Col md="2" className="d-flex align-items-end">
                    <Button color="primary" type="submit" disabled={inviting}>
                      {inviting ? "Sending..." : "Send Invitation"}
                    </Button>
                  </Col>
                </Row>
              </Form>}
            </CardBody>
          </Card>
        )}
      </>
    );
  };

  return (
    <>
      <SeoNoIndex noindex={true} />

      <div className="position-relative">
        <section className="section section-lg section-shaped pb-250">
          <div className="shape shape-style-1 shape-default">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
          <Container className="py-lg-md d-flex">
            <div className="col px-0">
              <Row>
                <Col lg="8">
                  <h1 className="display-3 text-white">
                    Building your song-flows{" "}
                    <span>your {accountLabel}</span>
                  </h1>
                </Col>
              </Row>
            </div>
          </Container>
          <div className="separator separator-bottom separator-skew">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
            >
              <polygon className="fill-white" points="2560 0 2560 100 0 100" />
            </svg>
          </div>
        </section>
      </div>
      <section className="section section-lg pt-lg-0 mt--200">
        <Container>
          <Row className="justify-content-center">
            <Col lg="12">{renderMainCard()}</Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default TeamAccount;
